import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Button,
  Row,
  Col,
  Image,
  Spinner,
} from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./DoctorForm.css";
import Avatar from "../../assets/avatar.png";
import CustomModal from "../../components/modal/Modal";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import {
  ACCEPTED_IMAGE_TYPES,
  getApiErrorMessage,
  prepareImageForUpload,
} from "../../utils/imageUpload";
import "react-toastify/dist/ReactToastify.css";
import { MapPin, Trash2, Info, Plus } from "lucide-react";
import { apiUrl, CONSULTATION_REGISTRATION } from "../../config/appConfig";

// `consultationLink`: rendered by the Tiryaq Professional consultation registration link, which
// submits to its own endpoint (the gateway records the source) and returns to that link.
const DoctorForm = ({ consultationLink = false }) => {
  const [fullName, setFullName] = useState("");
  const [profilePic, setProfilePic] = useState(null);
  const [profilePicUrl, setProfilePicUrl] = useState("");
  const [hospital, setHospital] = useState("");
  const [specialty, setSpecialty] = useState("");
  // Custom specialty entered by the doctor when "Other" is selected.
  const [customSpecialty, setCustomSpecialty] = useState("");
  const [onlineConsult, setOnlineConsult] = useState("No");
  const [phone, setPhone] = useState("");
  const [bio, setBio] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [yearsOfExperience, setYearOfExperience] = useState("");
  const [validated, setValidated] = useState(false);
  const fileInputRef = useRef(null);
  const [countryCode, setCountryCode] = useState("+964");
  const [showCamera, setShowCamera] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  // const [hospitals, setHospitals] = useState([""]);
  const [profilePicError, setProfilePicError] = useState("");
  // True while the selected photo is being downscaled. Submit is blocked during it, otherwise a
  // fast click would upload the original full-size file and defeat the optimisation.
  const [preparingPhoto, setPreparingPhoto] = useState(false);
  const [hasCamera, setHasCamera] = useState(true);
  const [hospitals, setHospitals] = useState([
    { name: "", address: "", latitude: null, longitude: null },
  ]);
  const [locationSuggestions, setLocationSuggestions] = useState([[]]);
  const sessionTokenRef = useRef(null);
  const dropdownRefs = useRef([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [specialties, setSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);
  // Ensures the edit pre-fill normalization runs only once after specialties load.
  const specialtyPrefilledRef = useRef(false);
  const [region, setRegion] = useState("Baghdad");

  // Iraqi regions list
  const iraqiRegions = [
    "Baghdad",
    "Basra",
    "Al-Anbar",
    "Babil (Babylon)",
    "Dhi Qar",
    "Al-Qadisiyyah (Diwaniyah)",
    "Diyala",
    "Dohuk",
    "Erbil",
    "Sulaymaniyah",
    "Halabja",
    "Kirkuk",
    "Karbala",
    "Najaf",
    "Nineveh (Mosul)",
    "Salah Al-Din",
    "Wasit",
    "Maysan",
    "Al-Muthanna"
  ];

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      dropdownRefs.current.forEach((ref, index) => {
        if (ref && !ref.contains(event.target)) {
          setLocationSuggestions((prev) => {
            const next = [...prev];
            next[index] = [];
            return next;
          });
        }
      });
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch specialties from API
  useEffect(() => {
    const fetchSpecialties = async () => {
      setLoadingSpecialties(true);
      try {
        const response = await axios.get(
          apiUrl('/specialties'),
          {
            headers: { accept: "application/json" },
          }
        );

        if (response.data.success && response.data.data?.specialties) {
          setSpecialties(response.data.data.specialties);
        } else {
          toast.error("Failed to load specialties");
          // Fallback to empty array
          setSpecialties([]);
        }
      } catch (error) {
        console.error("Error fetching specialties:", error);
        toast.error("Failed to load specialties");
        // Fallback to empty array
        setSpecialties([]);
      } finally {
        setLoadingSpecialties(false);
      }
    };

    fetchSpecialties();
  }, []);

  // Normalize an incoming specialty value once specialties are loaded.
  // - If it matches a predefined specialty (case-insensitive), align the
  //   dropdown to the canonical option.
  // - If it's a non-empty custom value not in the list, select "Other" and
  //   move the value into the custom-specialty text field (edit pre-fill).
  // No-op for the create flow (specialty starts empty).
  useEffect(() => {
    if (loadingSpecialties || specialtyPrefilledRef.current) return;
    if (!specialty || specialty === "Other") return;

    const match = specialties.find(
      (s) => s.toLowerCase() === specialty.toLowerCase()
    );
    if (match) {
      if (match !== specialty) setSpecialty(match);
    } else {
      setCustomSpecialty(specialty);
      setSpecialty("Other");
    }
    specialtyPrefilledRef.current = true;
  }, [loadingSpecialties, specialties, specialty]);

  // Fetch coordinates from placeId
  const fetchCoordinatesFromPlaceId = async (placeId) => {
    try {
      if (!window.google?.maps?.places) {
        return null;
      }

      const { Place } = await window.google.maps.importLibrary("places");

      const place = new Place({
        id: placeId,
      });

      await place.fetchFields({
        fields: ["location"],
      });

      if (place.location) {
        return {
          latitude: place.location.lat(),
          longitude: place.location.lng(),
        };
      }

      return null;
    } catch (error) {
      console.error("Error fetching coordinates:", error);
      return null;
    }
  };

  const fetchLocationSuggestions = async (index, value) => {
    if (!value.trim()) {
      setLocationSuggestions((prev) => {
        const next = [...prev];
        next[index] = [];
        return next;
      });
      return;
    }

    try {
      // Wait for Google Maps to be loaded
      if (!window.google?.maps?.places) {
        return;
      }

      // Create session token if not exists
      if (!sessionTokenRef.current && window.google?.maps?.places?.AutocompleteSessionToken) {
        sessionTokenRef.current = new window.google.maps.places.AutocompleteSessionToken();
      }

      // Use the new Places API with importLibrary
      const { AutocompleteSuggestion } = await window.google.maps.importLibrary("places");

      const request = {
        input: value,
        sessionToken: sessionTokenRef.current,
      };

      // Fetch suggestions using the new API
      const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      setLocationSuggestions((prev) => {
        const next = [...prev];
        next[index] = suggestions.map((suggestion) => {
          const placePrediction = suggestion.placePrediction;
          return {
            placeId: placePrediction.placeId,
            description: placePrediction.text.toString(),
          };
        });
        return next;
      });
    } catch (error) {
      console.error("Autocomplete suggestions error", error);
      setLocationSuggestions((prev) => {
        const next = [...prev];
        next[index] = [];
        return next;
      });
    }
  };

  const handleHospitalChange = (index, field, value, skipFetch = false) => {
    const updated = [...hospitals];
    updated[index][field] = value;
    setHospitals(updated);

    if (field === "address" && !skipFetch) {
      fetchLocationSuggestions(index, value);
    }
  };

  const handleAddHospital = () => {
    setHospitals([...hospitals, { name: "", address: "", latitude: null, longitude: null }]);
    setLocationSuggestions((prev) => [...prev, []]);
  };

  const handleRemoveHospital = (index) => {
    setHospitals(hospitals.filter((_, i) => i !== index));
    setLocationSuggestions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleOpenCamera = async () => {
    try {
      // Check if the device has any video input devices (camera)
      const devices = await navigator.mediaDevices.enumerateDevices();
      const hasVideoInput = devices.some(
        (device) => device.kind === "videoinput"
      );

      if (!hasVideoInput) {
        setHasCamera(false);
        toast.error("No camera detected on this device.");
        return;
      }

      setHasCamera(true);
      setShowCamera(true);

      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play();
      }
    } catch (err) {
      console.error("Error accessing camera:", err);
      setHasCamera(false);
      toast.error(
        "Unable to access camera. Please check permissions or device settings."
      );
    }
  };

  // Capture photo from camera
  const handleCapturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      const file = new File([blob], "camera-photo.png", { type: "image/png" });
      // No size check. A capture from a high-resolution camera is downscaled for the upload
      // instead of being refused — see prepareImageForUpload.
      const prepared = await prepareImageForUpload(file);
      setProfilePic(prepared);
      setProfilePicUrl(URL.createObjectURL(prepared));
      setProfilePicError("");
    });

    // Stop camera
    const stream = video.srcObject;
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
    }
    setShowCamera(false);
  };

  // const handleProfilePicChange = (e) => {
  //   const file = e.target.files[0];
  //   setProfilePic(file);
  //   if (file) {
  //     setProfilePicUrl(URL.createObjectURL(file));
  //   }
  // };

  const handleProfilePicChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Type is still checked — the server only signs an upload for an image.
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      const message = "Invalid file type. Only JPG, JPEG, PNG or WebP allowed.";
      setProfilePicError(message);
      toast.error(message);
      setProfilePic(null);
      setProfilePicUrl("");
      return;
    }

    // THERE IS NO SIZE LIMIT. The previous 10 MB rule rejected ordinary phone photos outright;
    // now any size is accepted and the image is downscaled for the upload instead, which is both
    // more permissive and faster than what the limit was protecting.
    setPreparingPhoto(true);
    try {
      const prepared = await prepareImageForUpload(file);
      setProfilePic(prepared);
      setProfilePicUrl(URL.createObjectURL(prepared));
      setProfilePicError("");
    } finally {
      setPreparingPhoto(false);
    }
  };

  const countryCodes = [
    { code: "+93", name: "Afghanistan" },
    { code: "+355", name: "Albania" },
    { code: "+213", name: "Algeria" },
    { code: "+1684", name: "American Samoa" },
    { code: "+376", name: "Andorra" },
    { code: "+244", name: "Angola" },
    { code: "+1264", name: "Anguilla" },
    { code: "+672", name: "Antarctica" },
    { code: "+1268", name: "Antigua and Barbuda" },
    { code: "+54", name: "Argentina" },
    { code: "+374", name: "Armenia" },
    { code: "+297", name: "Aruba" },
    { code: "+61", name: "Australia" },
    { code: "+43", name: "Austria" },
    { code: "+994", name: "Azerbaijan" },
    { code: "+1242", name: "Bahamas" },
    { code: "+973", name: "Bahrain" },
    { code: "+880", name: "Bangladesh" },
    { code: "+1246", name: "Barbados" },
    { code: "+375", name: "Belarus" },
    { code: "+32", name: "Belgium" },
    { code: "+501", name: "Belize" },
    { code: "+229", name: "Benin" },
    { code: "+1441", name: "Bermuda" },
    { code: "+975", name: "Bhutan" },
    { code: "+591", name: "Bolivia" },
    { code: "+387", name: "Bosnia and Herzegovina" },
    { code: "+267", name: "Botswana" },
    { code: "+55", name: "Brazil" },
    { code: "+246", name: "British Indian Ocean Territory" },
    { code: "+673", name: "Brunei Darussalam" },
    { code: "+359", name: "Bulgaria" },
    { code: "+226", name: "Burkina Faso" },
    { code: "+257", name: "Burundi" },
    { code: "+855", name: "Cambodia" },
    { code: "+237", name: "Cameroon" },
    { code: "+1", name: "Canada" },
    { code: "+238", name: "Cape Verde" },
    { code: "+1345", name: "Cayman Islands" },
    { code: "+236", name: "Central African Republic" },
    { code: "+235", name: "Chad" },
    { code: "+56", name: "Chile" },
    { code: "+86", name: "China" },
    { code: "+57", name: "Colombia" },
    { code: "+269", name: "Comoros" },
    { code: "+242", name: "Congo" },
    { code: "+243", name: "Congo, Democratic Republic" },
    { code: "+682", name: "Cook Islands" },
    { code: "+506", name: "Costa Rica" },
    { code: "+225", name: "Côte d'Ivoire" },
    { code: "+385", name: "Croatia" },
    { code: "+53", name: "Cuba" },
    { code: "+357", name: "Cyprus" },
    { code: "+420", name: "Czech Republic" },
    { code: "+45", name: "Denmark" },
    { code: "+253", name: "Djibouti" },
    { code: "+1767", name: "Dominica" },
    { code: "+1809", name: "Dominican Republic" },
    { code: "+593", name: "Ecuador" },
    { code: "+20", name: "Egypt" },
    { code: "+503", name: "El Salvador" },
    { code: "+240", name: "Equatorial Guinea" },
    { code: "+291", name: "Eritrea" },
    { code: "+372", name: "Estonia" },
    { code: "+251", name: "Ethiopia" },
    { code: "+500", name: "Falkland Islands" },
    { code: "+298", name: "Faroe Islands" },
    { code: "+679", name: "Fiji" },
    { code: "+358", name: "Finland" },
    { code: "+33", name: "France" },
    { code: "+594", name: "French Guiana" },
    { code: "+689", name: "French Polynesia" },
    { code: "+241", name: "Gabon" },
    { code: "+220", name: "Gambia" },
    { code: "+995", name: "Georgia" },
    { code: "+49", name: "Germany" },
    { code: "+233", name: "Ghana" },
    { code: "+350", name: "Gibraltar" },
    { code: "+30", name: "Greece" },
    { code: "+299", name: "Greenland" },
    { code: "+1473", name: "Grenada" },
    { code: "+590", name: "Guadeloupe" },
    { code: "+1671", name: "Guam" },
    { code: "+502", name: "Guatemala" },
    { code: "+224", name: "Guinea" },
    { code: "+245", name: "Guinea-Bissau" },
    { code: "+592", name: "Guyana" },
    { code: "+509", name: "Haiti" },
    { code: "+504", name: "Honduras" },
    { code: "+852", name: "Hong Kong" },
    { code: "+36", name: "Hungary" },
    { code: "+354", name: "Iceland" },
    { code: "+91", name: "India" },
    { code: "+62", name: "Indonesia" },
    { code: "+98", name: "Iran" },
    { code: "+964", name: "Iraq" },
    { code: "+353", name: "Ireland" },
    { code: "+972", name: "Israel" },
    { code: "+39", name: "Italy" },
    { code: "+1876", name: "Jamaica" },
    { code: "+81", name: "Japan" },
    { code: "+962", name: "Jordan" },
    { code: "+7", name: "Kazakhstan" },
    { code: "+254", name: "Kenya" },
    { code: "+686", name: "Kiribati" },
    { code: "+965", name: "Kuwait" },
    { code: "+996", name: "Kyrgyzstan" },
    { code: "+856", name: "Lao People's Democratic Republic" },
    { code: "+371", name: "Latvia" },
    { code: "+961", name: "Lebanon" },
    { code: "+266", name: "Lesotho" },
    { code: "+231", name: "Liberia" },
    { code: "+218", name: "Libya" },
    { code: "+423", name: "Liechtenstein" },
    { code: "+370", name: "Lithuania" },
    { code: "+352", name: "Luxembourg" },
    { code: "+853", name: "Macao" },
    { code: "+389", name: "Macedonia" },
    { code: "+261", name: "Madagascar" },
    { code: "+265", name: "Malawi" },
    { code: "+60", name: "Malaysia" },
    { code: "+960", name: "Maldives" },
    { code: "+223", name: "Mali" },
    { code: "+356", name: "Malta" },
    { code: "+692", name: "Marshall Islands" },
    { code: "+596", name: "Martinique" },
    { code: "+222", name: "Mauritania" },
    { code: "+230", name: "Mauritius" },
    { code: "+262", name: "Mayotte" },
    { code: "+52", name: "Mexico" },
    { code: "+691", name: "Micronesia" },
    { code: "+373", name: "Moldova" },
    { code: "+377", name: "Monaco" },
    { code: "+976", name: "Mongolia" },
    { code: "+382", name: "Montenegro" },
    { code: "+1664", name: "Montserrat" },
    { code: "+212", name: "Morocco" },
    { code: "+258", name: "Mozambique" },
    { code: "+95", name: "Myanmar" },
    { code: "+264", name: "Namibia" },
    { code: "+674", name: "Nauru" },
    { code: "+977", name: "Nepal" },
    { code: "+31", name: "Netherlands" },
    { code: "+599", name: "Netherlands Antilles" },
    { code: "+687", name: "New Caledonia" },
    { code: "+64", name: "New Zealand" },
    { code: "+505", name: "Nicaragua" },
    { code: "+227", name: "Niger" },
    { code: "+234", name: "Nigeria" },
    { code: "+683", name: "Niue" },
    { code: "+672", name: "Norfolk Island" },
    { code: "+850", name: "North Korea" },
    { code: "+1670", name: "Northern Mariana Islands" },
    { code: "+47", name: "Norway" },
    { code: "+968", name: "Oman" },
    { code: "+92", name: "Pakistan" },
    { code: "+680", name: "Palau" },
    { code: "+970", name: "Palestinian Territory" },
    { code: "+507", name: "Panama" },
    { code: "+675", name: "Papua New Guinea" },
    { code: "+595", name: "Paraguay" },
    { code: "+51", name: "Peru" },
    { code: "+63", name: "Philippines" },
    { code: "+48", name: "Poland" },
    { code: "+351", name: "Portugal" },
    { code: "+1", name: "Puerto Rico" },
    { code: "+974", name: "Qatar" },
    { code: "+262", name: "Réunion" },
    { code: "+40", name: "Romania" },
    { code: "+7", name: "Russia" },
    { code: "+250", name: "Rwanda" },
    { code: "+590", name: "Saint Barthélemy" },
    { code: "+290", name: "Saint Helena" },
    { code: "+1869", name: "Saint Kitts and Nevis" },
    { code: "+1758", name: "Saint Lucia" },
    { code: "+590", name: "Saint Martin" },
    { code: "+508", name: "Saint Pierre and Miquelon" },
    { code: "+1784", name: "Saint Vincent and the Grenadines" },
    { code: "+685", name: "Samoa" },
    { code: "+378", name: "San Marino" },
    { code: "+239", name: "Sao Tome and Principe" },
    { code: "+966", name: "Saudi Arabia" },
    { code: "+221", name: "Senegal" },
    { code: "+381", name: "Serbia" },
    { code: "+248", name: "Seychelles" },
    { code: "+232", name: "Sierra Leone" },
    { code: "+65", name: "Singapore" },
    { code: "+421", name: "Slovakia" },
    { code: "+386", name: "Slovenia" },
    { code: "+677", name: "Solomon Islands" },
    { code: "+252", name: "Somalia" },
    { code: "+27", name: "South Africa" },
    { code: "+82", name: "South Korea" },
    { code: "+34", name: "Spain" },
    { code: "+94", name: "Sri Lanka" },
    { code: "+249", name: "Sudan" },
    { code: "+597", name: "Suriname" },
    { code: "+268", name: "Swaziland" },
    { code: "+46", name: "Sweden" },
    { code: "+41", name: "Switzerland" },
    { code: "+963", name: "Syria" },
    { code: "+886", name: "Taiwan" },
    { code: "+992", name: "Tajikistan" },
    { code: "+255", name: "Tanzania" },
    { code: "+66", name: "Thailand" },
    { code: "+228", name: "Togo" },
    { code: "+690", name: "Tokelau" },
    { code: "+676", name: "Tonga" },
    { code: "+1868", name: "Trinidad and Tobago" },
    { code: "+216", name: "Tunisia" },
    { code: "+90", name: "Turkey" },
    { code: "+993", name: "Turkmenistan" },
    { code: "+1649", name: "Turks and Caicos Islands" },
    { code: "+688", name: "Tuvalu" },
    { code: "+256", name: "Uganda" },
    { code: "+380", name: "Ukraine" },
    { code: "+971", name: "United Arab Emirates" },
    { code: "+44", name: "United Kingdom" },
    { code: "+1", name: "United States" },
    { code: "+598", name: "Uruguay" },
    { code: "+998", name: "Uzbekistan" },
    { code: "+678", name: "Vanuatu" },
    { code: "+58", name: "Venezuela" },
    { code: "+84", name: "Vietnam" },
    { code: "+1284", name: "Virgin Islands, British" },
    { code: "+1340", name: "Virgin Islands, U.S." },
    { code: "+681", name: "Wallis and Futuna" },
    { code: "+967", name: "Yemen" },
    { code: "+260", name: "Zambia" },
    { code: "+263", name: "Zimbabwe" },
  ];


  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Validation check
    // if (form.checkValidity() === false || !profilePic) {
    //   e.stopPropagation();
    //   setValidated(true);
    //   return;
    // }

    if (form.checkValidity() === false || !profilePic) {
      e.stopPropagation();
      setValidated(true);
      if (!profilePic) setProfilePicError("Profile picture is required.");
      return;
    }

    if (profilePicError) {
      toast.error(profilePicError);
      return;
    }

    // When "Other" is selected the custom specialty text is mandatory.
    const isOtherSpecialty = specialty === "Other";
    const finalSpecialty = isOtherSpecialty ? customSpecialty.trim() : specialty;
    if (isOtherSpecialty && !finalSpecialty) {
      e.stopPropagation();
      setValidated(true);
      toast.error("Please enter your specialty");
      return;
    }


    setIsSubmitting(true);
    try {
      // Upload Profile Pic
      const formData = new FormData();
      formData.append("file", profilePic);

      const uploadResponse = await axios.post(
        apiUrl('/professionals/photo'),
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const profilePictureUrl = uploadResponse.data.data.key;

      // Create Doctor Data
      const doctorData = {
        role_id: 4,
        full_name: fullName,
        // hospital_name: hospitals.filter((h) => h !== ""),
        hospital_details: hospitals
          .filter((h) => h.name.trim() !== "" && h.address.trim() !== "")
          .map((h) => ({
            name: h.name.trim(),
            address: h.address.trim(),
            latitude: h.latitude,
            longitude: h.longitude,
          })),
        specialty: finalSpecialty.toLowerCase(),
        online_consultation: onlineConsult === "Yes",
        contact_phone: `${countryCode}${phone}`,
        // contact_phone: phone,
        profile_picture_url: profilePictureUrl,
        short_bio: bio,
        years_of_experience: parseInt(yearsOfExperience),
        region: region,
      };

      const createPath = consultationLink ? CONSULTATION_REGISTRATION.CREATE_PATH : '/professionals';
      await axios.post(apiUrl(createPath), doctorData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      setShowModal(true); // show success modal
      setFullName("");
      setProfilePic(null);
      setProfilePicUrl("");
      setHospital("");
      setHospitals([{ name: "", address: "", latitude: null, longitude: null }]);
      setLocationSuggestions([[]]);
      setSpecialty("");
      setCustomSpecialty("");
      setOnlineConsult("No");
      setPhone("");
      setBio("");
      setYearOfExperience("");
      setRegion("Baghdad");
      setValidated(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.success("Your details have been saved successfully");
    } catch (error) {
      // `toast.error(error)` was passing the axios Error OBJECT. react-toastify renders its
      // argument as a React child, and an Error is not a valid one, so the toast came up empty —
      // the failure looked silent even though this block ran. The message is extracted first.
      console.error("Error submitting the doctor registration form:", error);
      toast.error(
        getApiErrorMessage(
          error,
          "We could not submit your details. Please try again.",
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="main-container">
        <main className="main-content">
          <div className="content-wrapper doctor-form-wide">
            <div className="login-form-container">
              <h2 className="text-center">Doctor Online Consultation Details</h2>
              <Form noValidate validated={validated} onSubmit={handleSubmit}>
                {/* Profile Picture - top header */}
                <div className="doctor-profile-header">
                  <Image
                    src={profilePicUrl || Avatar}
                    roundedCircle
                    width={120}
                    height={120}
                    alt="Profile Preview"
                    className="doctor-profile-avatar"
                  />
                  <span className="doctor-profile-title">
                    Profile Picture<span className="required-asterisk">*</span>
                  </span>
                  <div className="doctor-profile-actions">
                    <Button
                      variant="outline-primary"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Upload from Device
                    </Button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      style={{ display: "none" }}
                      onChange={handleProfilePicChange}
                    />
                    <Button variant="outline-primary" onClick={handleOpenCamera}>
                      Open Camera
                    </Button>
                  </div>
                  {profilePicError && (
                    <div className="invalid-feedback d-block">
                      {profilePicError}
                    </div>
                  )}
                  {showCamera && (
                    <div className="doctor-profile-camera">
                      {hasCamera ? (
                        <>
                          <video
                            ref={videoRef}
                            style={{ width: "100%", maxHeight: 300 }}
                          />
                          <div className="d-flex justify-content-between mt-2">
                            <Button variant="success" onClick={handleCapturePhoto}>
                              Capture
                            </Button>
                            <Button
                              variant="danger"
                              onClick={() => {
                                const stream = videoRef.current?.srcObject;
                                if (stream)
                                  stream
                                    .getTracks()
                                    .forEach((track) => track.stop());
                                setShowCamera(false);
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                          <canvas ref={canvasRef} style={{ display: "none" }} />
                        </>
                      ) : (
                        <div className="text-danger mt-2">
                          Camera not available on this device.
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Row 1: Full Name | Specialty | Years of Experience */}
                <Row>
                  <Col md={4}>
                    <Form.Group className="form-group" controlId="doctorFullName">
                      <Form.Label>
                        Doctor Full Name
                        <span className="required-asterisk">*</span>
                      </Form.Label>
                      <Form.Control
                        className="form-input"
                        type="text"
                        placeholder="Enter your full name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        Full name is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="form-group" controlId="specialty">
                      <Form.Label>
                        Specialty<span className="required-asterisk">*</span>
                      </Form.Label>
                      <div className="select-wrapper">
                        <Form.Select
                          className="form-select"
                          value={specialty}
                          onChange={(e) => setSpecialty(e.target.value)}
                          required
                          disabled={loadingSpecialties}
                        >
                          <option value="">
                            {loadingSpecialties
                              ? "Loading specialties..."
                              : "Select specialty"}
                          </option>
                          {specialties.map((spec) => (
                            <option key={spec} value={spec}>
                              {spec}
                            </option>
                          ))}
                          <option value="Other">Other</option>
                        </Form.Select>
                        <span className="select-icon">&#9662;</span>
                      </div>
                      {specialty === "Other" && (
                        <Form.Control
                          className="form-input"
                          type="text"
                          placeholder="Enter your specialty"
                          value={customSpecialty}
                          onChange={(e) => setCustomSpecialty(e.target.value)}
                          required
                          style={{ marginTop: "0.5rem" }}
                        />
                      )}
                      <Form.Control.Feedback type="invalid">
                        Specialty is required
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>

                  <Col md={4}>
                    <Form.Group className="form-group" controlId="experience">
                      <Form.Label>
                        Years of Experience
                        <span className="required-asterisk">*</span>
                      </Form.Label>
                      <Form.Control
                        className="form-input"
                        type="number"
                        placeholder="Enter Years of Experience"
                        value={yearsOfExperience}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === "" || parseInt(val) >= 0) {
                            setYearOfExperience(val);
                          }
                        }}
                        required
                        min={0}
                      />
                      <Form.Control.Feedback type="invalid">
                        Years of experience must be 0 or greater
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Row 2: Contact Phone | Region */}
                <Row>
                  <Col md={6}>
                    <Form.Group
                      className="form-group form-group-check"
                      controlId="contactPhone"
                    >
                      <Form.Label>
                        Contact Phone
                        <span className="required-asterisk">*</span>
                      </Form.Label>
                      <Row className="g-2">
                        <Col xs={5}>
                          <Form.Select
                            value={countryCode}
                            onChange={(e) => setCountryCode(e.target.value)}
                            required
                            style={{
                              fontSize: "13px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {countryCodes.map((c, index) => (
                              <option
                                key={`${c.code}-${c.name}-${index}`}
                                value={c.code}
                              >
                                {c.code} - {c.name}
                              </option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col xs={7}>
                          <Form.Control
                            className="form-input"
                            type="tel"
                            placeholder="Enter phone"
                            value={phone}
                            onChange={(e) => {
                              let val = e.target.value;
                              if (!/^[0-9]*$/.test(val)) return;
                              setPhone(val);
                            }}
                            required
                            pattern="^[0-9]{8,15}$"
                          />
                        </Col>
                      </Row>
                      <Form.Control.Feedback type="invalid">
                        Please enter a valid phone number (8–15 digits)
                      </Form.Control.Feedback>
                      <div className="phone-note-banner">
                        <Info size={18} />
                        <span>
                          Please enter your personal mobile number. This number
                          will be used for management purposes only.
                        </span>
                      </div>
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="form-group" controlId="region">
                      <Form.Label>
                        Region<span className="required-asterisk">*</span>
                      </Form.Label>
                      <div className="select-wrapper">
                        <Form.Select
                          className="form-select"
                          value={region}
                          onChange={(e) => setRegion(e.target.value)}
                          required
                        >
                          {iraqiRegions.map((regionName) => (
                            <option key={regionName} value={regionName}>
                              {regionName}
                            </option>
                          ))}
                        </Form.Select>
                        <span className="select-icon">&#9662;</span>
                      </div>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Short Bio - full width */}
                <Form.Group className="form-group" controlId="bio">
                  <Form.Label>
                    Short Bio<span className="required-asterisk">*</span>
                  </Form.Label>
                  <Form.Control
                    className="form-input"
                    as="textarea"
                    rows={3}
                    placeholder="Tell us about your experience"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Short bio is required
                  </Form.Control.Feedback>
                </Form.Group>

                {/* Hospital / Clinic Name */}
                {/* <Form.Group className="form-group" controlId="hospitalName">
                  <Form.Label>Hospital / Clinic Name</Form.Label>
                  <Form.Control
                    className="form-input"
                    type="text"
                    placeholder="Enter hospital or clinic name"
                    value={hospital}
                    onChange={(e) => setHospital(e.target.value)}
                    required
                  />
                  <Form.Control.Feedback type="invalid">
                    Hospital name is required
                  </Form.Control.Feedback>
                </Form.Group> */}
                {/* <Form.Group className="form-group" controlId="hospitalName">
                  <Form.Label>Hospital / Clinic Name</Form.Label>
                  {hospitals.map((hospital, index) => (
                    <Row key={index} className="mb-2 align-items-center">
                      <Col xs={10}>
                        <Form.Control
                          type="text"
                          placeholder="Enter hospital or clinic name"
                          value={hospital}
                          onChange={(e) =>
                            handleHospitalChange(index, e.target.value)
                          }
                          required
                        />
                      </Col>
                      <Col xs={2}>
                        {hospitals.length > 1 && (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleRemoveHospital(index)}
                            title="Remove Hospital"
                          >
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </Col>
                    </Row>
                  ))}
                  <Button variant="secondary" onClick={handleAddHospital}>
                    Add Another Hospital
                  </Button>
                </Form.Group> */}
                <Form.Group className="form-group" controlId="hospitalName">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <Form.Label className="mb-0">
                      Hospital / Clinic Details
                      <span className="required-asterisk">*</span>
                    </Form.Label>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="add-hospital-btn"
                      onClick={handleAddHospital}
                      title="Add Another Hospital"
                    >
                      <Plus size={16} />
                    </Button>
                  </div>
                  {hospitals.map((item, index) => (
                    <div key={index} className="mb-3 border p-3 rounded" style={{ position: "relative" }}>
                      <Row className="g-2 align-items-start">
                        <Col md={5}>
                          <Form.Control
                            type="text"
                            placeholder="Hospital/Clinic name"
                            value={item.name}
                            onChange={(e) => handleHospitalChange(index, "name", e.target.value)}
                            required
                          />
                        </Col>
                        <Col md>
                      <div
                        style={{ position: "relative" }}
                        ref={(el) => (dropdownRefs.current[index] = el)}
                      >
                        <div style={{ position: "relative" }}>
                          <MapPin
                            size={18}
                            style={{
                              position: "absolute",
                              left: "12px",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color: "#6c757d",
                              zIndex: 1,
                              pointerEvents: "none"
                            }}
                          />
                          <Form.Control
                            type="text"
                            placeholder="Search location..."
                            value={item.address}
                            onChange={(e) => handleHospitalChange(index, "address", e.target.value)}
                            required
                            autoComplete="off"
                            style={{
                              paddingLeft: "40px",
                              borderRadius: locationSuggestions[index]?.length > 0 ? "8px 8px 0 0" : "8px",
                              border: locationSuggestions[index]?.length > 0 ? "1px solid #0d6efd" : "1px solid #ced4da",
                              transition: "all 0.2s ease"
                            }}
                          />
                        </div>
                        {locationSuggestions[index]?.length > 0 && (
                          <div
                            style={{
                              position: "absolute",
                              zIndex: 1000,
                              width: "100%",
                              backgroundColor: "#fff",
                              border: "1px solid #dee2e6",
                              borderTop: "none",
                              borderRadius: "0 0 8px 8px",
                              maxHeight: 280,
                              overflowY: "auto",
                              boxShadow: "0 8px 16px rgba(0,0,0,0.15)",
                            }}
                          >
                            {locationSuggestions[index].map((suggestion, idx) => (
                              <div
                                key={suggestion.placeId}
                                style={{
                                  padding: "12px 16px",
                                  cursor: "pointer",
                                  borderBottom: idx === locationSuggestions[index].length - 1 ? "none" : "1px solid #f0f0f0",
                                  background: "#fff",
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: "12px",
                                  transition: "all 0.2s ease",
                                }}
                                onClick={async (e) => {
                                  e.stopPropagation();

                                  // Update the input value without triggering fetch
                                  handleHospitalChange(index, "address", suggestion.description, true);

                                  // Fetch coordinates for the selected place
                                  const coords = await fetchCoordinatesFromPlaceId(suggestion.placeId);

                                  if (coords) {
                                    // Update hospital with coordinates
                                    setHospitals((prev) => {
                                      const updated = [...prev];
                                      updated[index] = {
                                        ...updated[index],
                                        latitude: coords.latitude,
                                        longitude: coords.longitude,
                                      };
                                      return updated;
                                    });
                                  }

                                  // Clear suggestions to close dropdown
                                  setLocationSuggestions((prev) => {
                                    const next = [...prev];
                                    next[index] = [];
                                    return next;
                                  });
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.background = "#f8f9fa";
                                  e.currentTarget.style.paddingLeft = "20px";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.background = "#fff";
                                  e.currentTarget.style.paddingLeft = "16px";
                                }}
                              >
                                <MapPin
                                  size={18}
                                  style={{
                                    color: "#0d6efd",
                                    marginTop: "2px",
                                    flexShrink: 0
                                  }}
                                />
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontSize: "14px",
                                    color: "#212529",
                                    lineHeight: "1.5",
                                    fontWeight: "400"
                                  }}>
                                    {suggestion.description}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                        </Col>
                        {hospitals.length > 1 && (
                          <Col xs="auto" className="d-flex align-items-start">
                            <Button
                              variant="danger"
                              size="sm"
                              onClick={() => handleRemoveHospital(index)}
                              title="Remove Hospital"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </Col>
                        )}
                      </Row>
                    </div>
                  ))}
                </Form.Group>





                <Button
                  variant="primary"
                  type="submit"
                  className="login-btn"
                  disabled={isSubmitting || preparingPhoto}
                >
                  {isSubmitting || preparingPhoto ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      {preparingPhoto ? "Processing photo..." : "Submitting..."}
                    </>
                  ) : (
                    "Submit"
                  )}
                </Button>
              </Form>
            </div>
          </div>
        </main>
      </div>
      <CustomModal
        showModal={showModal}
        onClose={() => setShowModal(false)}
        autoRedirect={true}
        redirectUrl={consultationLink ? CONSULTATION_REGISTRATION.RETURN_URL : process.env.REACT_APP_FRONTEND_URL}
      />
    </>
  );
};

export default DoctorForm;
